import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  GlobalOutlined,
  PlusOutlined,
  TrophyOutlined,
  AimOutlined,
  DeleteOutlined,
  CloudOutlined,
  EditOutlined
} from "@ant-design/icons";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  Button,
  Row,
  Col,
  Divider,
  Space,
  Tag,
  InputNumber,
  Tooltip,
  Card,
  Typography,
  Empty,
  Alert,
  Grid
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { showErrorNotification } from "../../../utils/notification";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const THEME_CONFIG = {
  INFO: { color: "blue", icon: <InfoCircleOutlined /> },
  SUCCESS: { color: "green", icon: <CheckCircleOutlined /> },
  WARNING: { color: "orange", icon: <WarningOutlined /> },
  ERROR: { color: "red", icon: <CloseCircleOutlined /> },
  CRITICAL: { color: "volcano", icon: <CloseCircleOutlined /> }
};

const PRIORITY_MIN = 1;
const PRIORITY_MAX = 1000;

const CreateEditAnnouncementModal = ({
  isModalOpen = false,
  initialValues = null,
  setIsModalOpen = () => {},
  onSubmit = () => {},
  conflictError = null,
  ROOT_URL
}) => {
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isMobileView = screens.md === false;

  const [subdomainTargeting, setSubdomainTargeting] = useState([]);
  const [targeting, setTargeting] = useState([]);

  const [newTargetPath, setNewTargetPath] = useState("");
  const [newTargetMatchType, setNewTargetMatchType] = useState("EXACT");
  const [legacyManualMode, setLegacyManualMode] = useState(false);
  const [subdomainPathInputs, setSubdomainPathInputs] = useState({});
  const [subdomainManualModes, setSubdomainManualModes] = useState({});
  const [pendingSubdomain, setPendingSubdomain] = useState(null);
  const [coursesList, setCoursesList] = useState([]);
  const [subdomainOptions, setSubdomainOptions] = useState([]);

  // subdomainPathOptions: { [subdomainKey]: Array<{ label, value }> } — from backend
  const [subdomainPathOptions, setSubdomainPathOptions] = useState({});
  const [fetchingRequisites, setFetchingRequisites] = useState(false);

  const handleAddLegacyTarget = () => {
    if (!newTargetPath) {
      return;
    }
    let path = newTargetPath.trim();
    if (!path.startsWith("/")) {
      path = "/" + path;
    }

    const isPathAlreadyTargeted = targeting.some(
      (targetRule) => targetRule.path === path && targetRule.matchType === newTargetMatchType
    );
    if (isPathAlreadyTargeted) {
      return;
    }

    setTargeting((prevTargeting) => [...prevTargeting, { path, matchType: newTargetMatchType }]);
    setNewTargetPath("");
  };

  const handleRemoveLegacyTarget = (targetIndexToRemove) => {
    setTargeting((prevTargeting) =>
      prevTargeting.filter((_, index) => index !== targetIndexToRemove)
    );
  };

  const handleAddSubdomainEntry = () => {
    if (!pendingSubdomain) {
      return;
    }

    const isSubdomainAlreadyAdded = subdomainTargeting.some(
      (targetingEntry) => targetingEntry.subdomain === pendingSubdomain
    );
    if (isSubdomainAlreadyAdded) {
      return;
    }

    setSubdomainTargeting((prevSubdomainTargeting) => [
      ...prevSubdomainTargeting,
      { subdomain: pendingSubdomain, paths: [] }
    ]);
    setPendingSubdomain(null);
  };

  const handleRemoveSubdomainEntry = (subdomainToRemove) => {
    setSubdomainTargeting((prevSubdomainTargeting) =>
      prevSubdomainTargeting.filter(
        (targetingEntry) => targetingEntry.subdomain !== subdomainToRemove
      )
    );
    setSubdomainPathInputs((prevPathInputs) => {
      const updatedPathInputs = { ...prevPathInputs };
      delete updatedPathInputs[subdomainToRemove];
      return updatedPathInputs;
    });
  };

  const handleAddPathToEntry = (subdomain) => {
    const pathInputState = subdomainPathInputs[subdomain] ?? {
      path: "",
      matchType: "EXACT"
    };
    if (!pathInputState.path) {
      return;
    }

    let path = pathInputState.path.trim();
    if (!path.startsWith("/")) {
      path = "/" + path;
    }

    setSubdomainTargeting((prevSubdomainTargeting) =>
      prevSubdomainTargeting.map((targetingEntry) => {
        if (targetingEntry.subdomain !== subdomain) {
          return targetingEntry;
        }

        const isPathAlreadyAdded = targetingEntry.paths.some(
          (pathRule) => pathRule.path === path && pathRule.matchType === pathInputState.matchType
        );
        if (isPathAlreadyAdded) {
          return targetingEntry;
        }

        return {
          ...targetingEntry,
          paths: [...targetingEntry.paths, { path, matchType: pathInputState.matchType }]
        };
      })
    );

    setSubdomainPathInputs((prevPathInputs) => ({
      ...prevPathInputs,
      [subdomain]: { ...pathInputState, path: "" }
    }));
  };

  const handleRemovePathFromEntry = (subdomain, pathIndexToRemove) => {
    setSubdomainTargeting((prevSubdomainTargeting) =>
      prevSubdomainTargeting.map((targetingEntry) => {
        if (targetingEntry.subdomain !== subdomain) {
          return targetingEntry;
        }
        return {
          ...targetingEntry,
          paths: targetingEntry.paths.filter((_, index) => index !== pathIndexToRemove)
        };
      })
    );
  };

  const updateSubdomainPathInput = (subdomain, pathInputPatch) => {
    setSubdomainPathInputs((prevPathInputs) => ({
      ...prevPathInputs,
      [subdomain]: {
        ...(prevPathInputs[subdomain] ?? { path: "", matchType: "EXACT" }),
        ...pathInputPatch
      }
    }));
  };

  const handleFinish = (formValues) => {
    const { startDate, expiresAt, allowedCourseIds = [] } = formValues;
    const formattedValues = {
      ...formValues,
      allowedCourseIds,
      subdomainTargeting,
      targeting,
      startDate: startDate.toDate(),
      expiresAt: expiresAt ? expiresAt.toDate() : null
    };
    onSubmit(formattedValues);
  };

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    setFetchingRequisites(true);
    axios
      .get(`${ROOT_URL}/admin/website-announcement/form-requisites`)
      .then((response) => {
        const {
          coursesList = [],
          subdomainOptions = [],
          subdomainPathOptions = {}
        } = response.data || {};
        setCoursesList(coursesList);
        setSubdomainOptions(subdomainOptions);
        setSubdomainPathOptions(subdomainPathOptions);
      })
      .catch(showErrorNotification)
      .finally(() => {
        setFetchingRequisites(false);
      });

    setSubdomainPathInputs({});
    setPendingSubdomain(null);

    if (initialValues) {
      const {
        allowedCourseIds = [],
        priority = PRIORITY_MIN,
        startDate,
        expiresAt,
        subdomainTargeting: initialSubdomains = [],
        targeting: initialTargeting = []
      } = initialValues;

      form.setFieldsValue({
        ...initialValues,
        allowedCourseIds,
        priority,
        startDate: startDate ? dayjs(startDate) : dayjs(),
        expiresAt: expiresAt ? dayjs(expiresAt) : null
      });

      setSubdomainTargeting(Array.isArray(initialSubdomains) ? initialSubdomains : []);
      setTargeting(Array.isArray(initialTargeting) ? initialTargeting : []);
    } else {
      form.resetFields();
      form.setFieldsValue({
        isActive: false,
        isDismissible: true,
        theme: "INFO",
        priority: PRIORITY_MIN,
        allowedCourseIds: [],
        startDate: dayjs()
      });

      setSubdomainTargeting([]);
      setTargeting([]);
    }

    // Reset manual-entry modes when modal opens.
    setLegacyManualMode(false);
    setSubdomainManualModes({});
  }, [isModalOpen, initialValues, form, ROOT_URL]);

  const isEditing = Boolean(initialValues?.key);
  const usedSubdomains = subdomainTargeting.map((targetingEntry) => targetingEntry.subdomain);
  const availableSubdomainOptions = subdomainOptions.filter(
    (subdomainOption) => !usedSubdomains.includes(subdomainOption.value)
  );

  return (
    <Modal
      title={isEditing ? "Update Website Announcement" : "Create New Announcement"}
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      width={720}
      footer={[
        <Button key="back" onClick={() => setIsModalOpen(false)}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          {isEditing ? "Update Changes" : "Create Announcement"}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Divider orientation="left" style={{ marginTop: 0 }}>
          <InfoCircleOutlined /> Content &amp; Style
        </Divider>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={12}>
            <Form.Item label="Theme" name="theme" rules={[{ required: true }]}>
              <Select>
                {Object.entries(THEME_CONFIG).map(([key, config]) => (
                  <Option key={key} value={key}>
                    <Space>
                      <Tag color={config.color} icon={config.icon}>
                        {key}
                      </Tag>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch checkedChildren="Live" unCheckedChildren="Draft" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="Dismissible" name="isDismissible" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Message" name="message" rules={[{ required: true, max: 500 }]}>
          <TextArea rows={3} placeholder="Type the announcement message..." />
        </Form.Item>

        <Divider orientation="left">
          <TrophyOutlined /> Priority
        </Divider>

        {conflictError && (
          <Alert
            message="Priority Collision"
            description={conflictError}
            type="error"
            showIcon
            closable={false}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              label={
                <Tooltip title="Higher value = shown first. Targeted announcements always beat global ones. Two announcements in the same scope with the same priority and overlapping time window must not cover the same page.">
                  Priority (1–1000) ℹ️
                </Tooltip>
              }
              name="priority"
              rules={[
                { required: true, message: "Priority is required" },
                {
                  type: "number",
                  min: PRIORITY_MIN,
                  max: PRIORITY_MAX,
                  message: `Must be ${PRIORITY_MIN}–${PRIORITY_MAX}`
                }
              ]}
            >
              <InputNumber
                min={PRIORITY_MIN}
                max={PRIORITY_MAX}
                style={{ width: "100%" }}
                placeholder="e.g. 100"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">
          <GlobalOutlined /> Audience
        </Divider>

        <Form.Item
          label="Allowed Courses"
          name="allowedCourseIds"
          tooltip="Empty = visible to ALL users. Non-empty = only users subscribed to at least one of these courses."
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Select courses or leave empty for all users"
            loading={fetchingRequisites}
            options={coursesList}
            optionFilterProp="label"
          />
        </Form.Item>

        <Divider orientation="left">
          <AimOutlined /> Subdomain Targeting
        </Divider>

        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Empty = global (all subdomains). Each entry restricts the announcement to a specific
          subdomain. Leaving an entry&apos;s paths empty shows it on <em>all pages</em> of that
          subdomain.
        </Text>

        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Select subdomain"
            value={pendingSubdomain}
            onChange={setPendingSubdomain}
            loading={fetchingRequisites}
            style={{ width: "min(220px, 100%)" }}
            popupMatchSelectWidth={false}
            options={availableSubdomainOptions}
          />
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddSubdomainEntry}
            disabled={!pendingSubdomain}
          >
            Add Subdomain Rule
          </Button>
        </Space>

        {subdomainTargeting.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">No subdomain rules — announcement uses global targeting.</Text>
            }
            style={{ marginBottom: 12 }}
          />
        ) : (
          <Space direction="vertical" style={{ width: "100%", marginBottom: 8 }}>
            {subdomainTargeting.map((entry) => {
              const inputState = subdomainPathInputs[entry.subdomain] ?? {
                path: "",
                matchType: "EXACT"
              };
              const subdomainLabel =
                subdomainOptions.find((opt) => opt.value === entry.subdomain)?.label ??
                entry.subdomain;

              return (
                <Card
                  key={entry.subdomain}
                  size="small"
                  title={
                    <Space>
                      <CloudOutlined />
                      <Text strong>{subdomainLabel}</Text>
                      {entry.paths.length === 0 && <Tag color="green">All Pages</Tag>}
                    </Space>
                  }
                  extra={
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveSubdomainEntry(entry.subdomain)}
                    />
                  }
                  style={{ marginBottom: 4 }}
                >
                  <div style={{ marginBottom: 8 }}>
                    {entry.paths.length === 0 ? (
                      <Tag icon={<GlobalOutlined />} color="default">
                        Matching all pages on {subdomainLabel}
                      </Tag>
                    ) : (
                      entry.paths.map((pathRule, pathIndex) => (
                        <Tag
                          key={`${pathRule.path}::${pathRule.matchType}`}
                          closable
                          onClose={() => handleRemovePathFromEntry(entry.subdomain, pathIndex)}
                          color="blue"
                          style={{ marginBottom: 4 }}
                        >
                          {pathRule.path}
                          {pathRule.matchType === "SUBPAGES" ? "/*" : " (exact)"}
                        </Tag>
                      ))
                    )}
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, width: "100%" }}>
                    {subdomainManualModes[entry.subdomain] ? (
                      <Input
                        placeholder="/custom-path"
                        value={inputState.path}
                        onChange={(e) =>
                          updateSubdomainPathInput(entry.subdomain, { path: e.target.value })
                        }
                        onPressEnter={(e) => {
                          e.preventDefault();
                          handleAddPathToEntry(entry.subdomain);
                        }}
                        style={{ flex: isMobileView ? "1 1 100%" : 1, minWidth: 0 }}
                      />
                    ) : (
                      <Select
                        showSearch
                        placeholder="Select a path"
                        value={inputState.path || undefined}
                        onChange={(val) => updateSubdomainPathInput(entry.subdomain, { path: val })}
                        options={subdomainPathOptions[entry.subdomain] ?? []}
                        style={{ flex: isMobileView ? "1 1 100%" : 1, minWidth: 0 }}
                        popupMatchSelectWidth={false}
                        optionFilterProp="label"
                      />
                    )}
                    <Tooltip
                      title={
                        subdomainManualModes[entry.subdomain]
                          ? "Switch to path picker"
                          : "Enter path manually"
                      }
                    >
                      <Button
                        icon={<EditOutlined />}
                        onClick={() =>
                          setSubdomainManualModes((prev) => ({
                            ...prev,
                            [entry.subdomain]: !prev[entry.subdomain]
                          }))
                        }
                      />
                    </Tooltip>
                    <Select
                      value={inputState.matchType}
                      onChange={(matchType) =>
                        updateSubdomainPathInput(entry.subdomain, { matchType })
                      }
                      style={{ width: "min(150px, 100%)", flexShrink: 0 }}
                      popupMatchSelectWidth={false}
                    >
                      <Option value="EXACT">Exact Page</Option>
                      <Option value="SUBPAGES">And Sub-pages</Option>
                    </Select>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => handleAddPathToEntry(entry.subdomain)}
                      style={{ flexShrink: 0 }}
                    >
                      Add
                    </Button>
                  </div>
                </Card>
              );
            })}
          </Space>
        )}

        <Divider orientation="left">
          <LinkOutlined /> Page Targeting
          <Tooltip title="Legacy: applies to all subdomains. Prefer Subdomain Targeting above for per-subdomain control.">
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
              (all subdomains)
            </Text>
          </Tooltip>
        </Divider>

        <Form.Item
          label="Target Pages"
          tooltip="Empty = global (all pages). These rules apply across ALL subdomains."
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, width: "100%" }}>
            {legacyManualMode ? (
              <Input
                placeholder="/custom-path"
                value={newTargetPath}
                onChange={(e) => setNewTargetPath(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  handleAddLegacyTarget();
                }}
                style={{ flex: isMobileView ? "1 1 100%" : 1, minWidth: 0 }}
              />
            ) : (
              <Select
                showSearch
                placeholder="Select a path"
                value={newTargetPath || undefined}
                onChange={setNewTargetPath}
                options={subdomainPathOptions["main"] ?? []}
                style={{ flex: isMobileView ? "1 1 100%" : 1, minWidth: 0 }}
                popupMatchSelectWidth={false}
                optionFilterProp="label"
              />
            )}
            <Tooltip title={legacyManualMode ? "Switch to path picker" : "Enter path manually"}>
              <Button
                icon={<EditOutlined />}
                onClick={() => setLegacyManualMode((prev) => !prev)}
                style={{ flexShrink: 0 }}
              />
            </Tooltip>
            <Select
              value={newTargetMatchType}
              onChange={setNewTargetMatchType}
              style={{ width: "min(160px, 100%)", flexShrink: 0 }}
              popupMatchSelectWidth={false}
            >
              <Option value="EXACT">Exact Page</Option>
              <Option value="SUBPAGES">And Sub-pages</Option>
            </Select>
            <Button icon={<PlusOutlined />} onClick={handleAddLegacyTarget} style={{ flexShrink: 0 }}>
              Add
            </Button>
          </div>

          <div style={{ marginTop: "12px", minHeight: "32px" }}>
            {targeting.length === 0 ? (
              <Tag icon={<GlobalOutlined />}>Showing Site-wide (Global)</Tag>
            ) : (
              targeting.map((target, index) => (
                <Tag
                  key={`${target.path}::${target.matchType}`}
                  closable
                  onClose={() => handleRemoveLegacyTarget(index)}
                  color="blue"
                  style={{ marginBottom: "4px" }}
                >
                  {target.path}
                  {target.matchType === "SUBPAGES" ? " (+ sub-pages)" : " (exact)"}
                </Tag>
              ))
            )}
          </div>
        </Form.Item>

        <Divider orientation="left">Timing</Divider>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item label="Starts On" name="startDate" rules={[{ required: true }]}>
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Expires On" name="expiresAt">
              <DatePicker showTime style={{ width: "100%" }} placeholder="Run indefinitely" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateEditAnnouncementModal;
